import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Card, Flex, Text } from 'core/components';
import { useFormContext } from 'core/form/FormContext';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import FilterGroupConnector from 'app/components/filters/FilterGroupConnector';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';
import UdeFilterGroup from './UdeFilterGroup';
import { FilterGroupsField, ConnectorField, FormType } from './filter.types';

interface UdeFilterOptionsProps {
  fieldName?: string;
  filterGroupsField?: FilterGroupsField;
  connectorField?: ConnectorField;
  measurement: string | null;
  measurements: Measurement[];
  measurementHash: MeasurementHash;
  showTopLevelConnector?: boolean;
  adHocLabel?: string;
}

const UdeFilterOptions = observer(
  ({
    fieldName = 'filters',
    filterGroupsField,
    connectorField,
    measurement,
    measurements,
    measurementHash,
    showTopLevelConnector = true,
    adHocLabel = 'Filter Groups'
  }: UdeFilterOptionsProps) => {
    const form = useFormContext() as FormType | null;

    const filterGroups = (filterGroupsField ||
      (form?.getField(`${fieldName}.filterGroups`) as FilterGroupsField | undefined)) as FilterGroupsField | undefined;

    const groupConnector = (connectorField ||
      (form?.getField(`${fieldName}.connector`) as ConnectorField | undefined)) as ConnectorField | undefined;

    const handleAddFilterGroup = useCallback(() => {
      if (!filterGroups) {
        return;
      }

      // Add a new filter group with one empty filter
      filterGroups.add({
        filters: [{ filterField: '', operator: '=', filterValue: '' }],
        connector: 'All',
        not: false
      });
    }, [filterGroups]);

    const handleRemoveAllFilterGroups = useCallback(() => {
      if (filterGroups) {
        filterGroups.setValue([]);
      }
    }, [filterGroups]);

    if (!filterGroups) {
      return null;
    }

    const filterGroupsValue = filterGroups.getValue() || [];
    const numFilterGroups = filterGroupsValue.length;

    return (
      <Box>
        {/* Header */}
        <Flex mb={1} gap={1} alignItems="center">
          <Text as="div" fontWeight="medium" fontSize="small">
            {adHocLabel}
          </Text>

          {showTopLevelConnector && numFilterGroups > 1 && groupConnector && (
            <FilterGroupConnector connector={groupConnector} readOnly={false} />
          )}

          <Flex flex={1} justifyContent="flex-end">
            {numFilterGroups > 0 && (
              <Button
                onClick={handleRemoveAllFilterGroups}
                disabled={numFilterGroups === 0}
                text="Remove All Groups"
                small
                intent="danger"
                minimal
              />
            )}
          </Flex>
        </Flex>

        {/* Empty state */}
        {numFilterGroups === 0 && (
          <Card textAlign="center" p={2}>
            <Text mb={1} muted>
              No filter groups have been applied
            </Text>
            <Flex mt={1} alignItems="center" justifyContent="center">
              <Button onClick={handleAddFilterGroup} icon="plus">
                Add Filter Group
              </Button>
            </Flex>
          </Card>
        )}

        {/* Filter groups */}
        {numFilterGroups > 0 && (
          <Box>
            {filterGroups.map((group, index) => (
              <UdeFilterGroup
                key={group.connector._id}
                group={group}
                index={index}
                groups={filterGroups}
                measurement={measurement}
                measurements={measurements}
                measurementHash={measurementHash}
                fieldName={fieldName}
              />
            ))}

            <Flex mt={1}>
              <Button icon="plus" onClick={handleAddFilterGroup} small>
                Add Filter Group
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
    );
  }
);

export default UdeFilterOptions;
